import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../API';
import { regionRefsEqual, toRegionRef } from '../definition/regionIdentity';
import { regionDisplayName } from './utils/formatRegion';
import RegionMembershipList from './RegionMembershipList';
import styles from './blocks.module.css';

/**
 * Region membership picker (templates + schema-only options).
 * Create mode: local selection via selectedRegionRefs.
 * Edit mode: parent handles live add/remove via onToggleRegion.
 *
 * Separates "In this block" from the add catalog; Configure details is explicit.
 */
export default function BlockRegionsEditor({
    isEditMode = false,
    selectedRegionRefs = [],
    blockRegions = [],
    onToggleRegion,
    onConfigureRegion,
    disabled = false,
    highlightSchemaKey = null,
    caseScoped = false,
}) {
    const membershipCount = isEditMode
        ? (blockRegions?.length || 0)
        : (selectedRegionRefs?.length || 0);

    const [pickerOpen, setPickerOpen] = useState(() => membershipCount === 0);
    const [regionFilter, setRegionFilter] = useState('');

    const { data: regionsResponse, isLoading: regionsLoading } = useQuery({
        queryKey: ['bdsa-region'],
        queryFn: () => api.get('/bdsa/region/template'),
        staleTime: 5 * 60 * 1000,
    });

    const { data: schemaResponse } = useQuery({
        queryKey: ['bdsa-schema', 'region'],
        queryFn: () => api.get('/bdsa/schema', { query: { name: 'region', includeDrafts: false } }),
        staleTime: 5 * 60 * 1000,
    });

    const templateRegions = regionsResponse?.status === 200 && Array.isArray(regionsResponse.data)
        ? regionsResponse.data
        : [];

    const latestSchema = useMemo(() => {
        if (schemaResponse?.status !== 200 || !Array.isArray(schemaResponse.data) || schemaResponse.data.length === 0) {
            return null;
        }
        return [...schemaResponse.data].sort((a, b) => (b.version || 0) - (a.version || 0))[0];
    }, [schemaResponse]);

    const schemaRegionList = useMemo(() => {
        if (!latestSchema) return [];
        const regionProperties = latestSchema.definition?.properties?.bdsa_region_ids?.properties?.regions?.properties || {};
        return Object.entries(regionProperties).map(([key, value]) => ({
            key,
            schemaRegionKey: key,
            title: value.title || key,
            displayName: value.title || key,
            abbreviation: value.abbreviation || '',
        }));
    }, [latestSchema]);

    const templateSchemaKeys = useMemo(
        () => new Set(templateRegions.map((r) => r.schemaRegionKey).filter(Boolean)),
        [templateRegions]
    );

    const availableRegions = useMemo(() => {
        const combined = [...templateRegions];
        schemaRegionList
            .filter((sr) => !templateSchemaKeys.has(sr.key))
            .forEach((sr) => {
                combined.push({
                    schemaRegionKey: sr.key,
                    displayName: sr.displayName || sr.title || sr.key,
                    abbreviation: sr.abbreviation || sr.key.slice(0, 4).toUpperCase(),
                    _id: undefined,
                });
            });
        return combined;
    }, [templateRegions, schemaRegionList, templateSchemaKeys]);

    const inBlockItems = useMemo(() => {
        if (isEditMode) {
            return blockRegions.map((br) => ({
                key: br._id || br.schemaRegionKey,
                label: regionDisplayName(br) || br.schemaRegionKey,
                schemaRegionKey: br.schemaRegionKey,
                regionDoc: br,
                catalogMatch: availableRegions.find((r) => r.schemaRegionKey === br.schemaRegionKey) || null,
            }));
        }
        return selectedRegionRefs.map((ref) => {
            const match = availableRegions.find((r) => {
                const candidate = r._id ? toRegionRef(r, 'template') : toRegionRef(r, 'schema');
                return candidate && regionRefsEqual(candidate, ref);
            });
            return {
                key: ref.kind === 'template' ? ref.id : ref.key,
                label: match ? (regionDisplayName(match) || match.schemaRegionKey) : (ref.key || ref.id),
                schemaRegionKey: match?.schemaRegionKey || (ref.kind === 'schema' ? ref.key : null),
                regionDoc: match
                    ? {
                        ...match,
                        _id: match._id || `draft:${ref.kind === 'template' ? ref.id : ref.key}`,
                        __draftRef: ref,
                    }
                    : { _id: `draft:${ref.kind === 'template' ? ref.id : ref.key}`, __draftRef: ref },
                catalogMatch: match,
            };
        });
    }, [isEditMode, blockRegions, selectedRegionRefs, availableRegions]);

    const selectedSchemaKeys = useMemo(() => {
        if (isEditMode) {
            return new Set(blockRegions.map((br) => br.schemaRegionKey).filter(Boolean));
        }
        const keys = new Set();
        selectedRegionRefs.forEach((ref) => {
            if (ref?.kind === 'schema' && ref.key) keys.add(ref.key);
            if (ref?.kind === 'template') {
                const match = availableRegions.find((r) => r._id === ref.id);
                if (match?.schemaRegionKey) keys.add(match.schemaRegionKey);
            }
        });
        return keys;
    }, [isEditMode, blockRegions, selectedRegionRefs, availableRegions]);

    const catalogRegions = useMemo(
        () => availableRegions.filter((r) => !selectedSchemaKeys.has(r.schemaRegionKey)),
        [availableRegions, selectedSchemaKeys]
    );

    const filteredCatalog = useMemo(() => {
        const q = regionFilter.trim().toLowerCase();
        if (!q) return catalogRegions;
        return catalogRegions.filter((region) => {
            const hay = [
                region.displayName,
                region.abbreviation,
                region.schemaRegionKey,
            ].filter(Boolean).join(' ').toLowerCase();
            return hay.includes(q);
        });
    }, [catalogRegions, regionFilter]);

    if (regionsLoading) {
        return <p className={styles.jobText}>Loading regions…</p>;
    }

    const removeHint = caseScoped
        ? 'Remove from this case’s block'
        : 'Remove from this block';

    return (
        <div>
            <div className={styles.membershipSection}>
                <div className={styles.formLabel}>
                    In this block {isEditMode ? '' : '*'}
                </div>
                <RegionMembershipList
                    items={inBlockItems.map((item) => ({
                        ...item,
                        highlighted: !!(highlightSchemaKey && item.schemaRegionKey === highlightSchemaKey),
                    }))}
                    emptyText={isEditMode ? 'No regions on this block yet.' : 'Add at least one region.'}
                    onConfigure={onConfigureRegion
                        ? (item) => onConfigureRegion(item.regionDoc)
                        : null}
                    onRemove={(item) => {
                        const option = item.catalogMatch
                            || { schemaRegionKey: item.schemaRegionKey, _id: item.regionDoc?._id };
                        onToggleRegion?.(option);
                    }}
                    removeTitle={removeHint}
                    disabled={disabled}
                    hideConfigure={!onConfigureRegion}
                />
            </div>

            <div className={styles.membershipSection}>
                <button
                    type="button"
                    className={styles.buttonSecondary}
                    onClick={() => setPickerOpen((open) => !open)}
                    disabled={disabled}
                    aria-expanded={pickerOpen}
                >
                    {pickerOpen ? 'Hide region list' : 'Add region'}
                </button>

                {pickerOpen && (
                    <div className={styles.regionPickerPanel}>
                        <input
                            type="search"
                            className={styles.regionFilterInput}
                            value={regionFilter}
                            onChange={(e) => setRegionFilter(e.target.value)}
                            placeholder="Filter regions…"
                            disabled={disabled}
                            aria-label="Filter available regions"
                        />
                        <div className={styles.regionOptionList} role="listbox" aria-label="Available regions">
                            {catalogRegions.length === 0 ? (
                                <p className={styles.jobText}>All available regions are already on this block.</p>
                            ) : filteredCatalog.length === 0 ? (
                                <p className={styles.jobText}>No regions match that filter.</p>
                            ) : (
                                filteredCatalog.map((region) => {
                                    const key = region._id || region.schemaRegionKey;
                                    return (
                                        <button
                                            key={key}
                                            type="button"
                                            role="option"
                                            aria-selected={false}
                                            className={styles.regionOption}
                                            disabled={disabled}
                                            onClick={() => onToggleRegion?.(region)}
                                        >
                                            <span>{region.displayName || region.abbreviation || key}</span>
                                            {region.abbreviation && region.displayName && (
                                                <span className={styles.regionOptionMeta}>{region.abbreviation}</span>
                                            )}
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
